// src/components/RestaurantPanel.jsx
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUser, API_BASE } from "../context/UserContext";
import SavePlaceModal from "./SavePlaceModal";

import { STATUS_LABEL, STATUS_COLOR, FRONTEND_URL, BEST_CATEGORIES } from "../constants";
import generateShareCard from "../utils/generateShareCard";

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";
const C = {
  primary:          "#655d54",
  primaryDim:       "#595149",
  primaryContainer: "#ede0d5",
  bg:               "#faf9f6",
  surfaceLow:       "#f4f4f0",
  surfaceLowest:    "#ffffff",
  container:        "#edeeea",
  onSurface:        "#2f3430",
  onSurfaceVariant: "#5c605c",
  outlineVariant:   "#8a8e8a",
  error:            "#9e422c",
};

const isMobile = () => window.innerWidth <= 768;



export default function RestaurantPanel({
  restaurant, onClose, onHide, sidebarWidth = 240,
  onPlaceUpdated, mapInstance, onDataChange,
  myBestPicks = {}, onBestPickAdd, onBestPickRemove,
  onViewUserProfile, canGoBack = false, onGoBack,
}) {
  const { user } = useUser();
  const { t } = useTranslation();
  const mobile = isMobile();
  const [r, setR] = useState(restaurant);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [neighbors, setNeighbors] = useState([]);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showMapMenu, setShowMapMenu] = useState(false);
  const [showBestMenu, setShowBestMenu] = useState(false);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [imgPortrait, setImgPortrait] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // { id, author_nickname }
  const commentInputRef = useRef(null);

  // Mobile sheet state: "peek" | "full"
  const [sheetMode, setSheetMode] = useState("peek");
  const [peekHeight, setPeekHeight] = useState(220);
  const [addToMyPlaceOpen, setAddToMyPlaceOpen] = useState(false);
  const sheetRef = useRef(null);
  const peekEndRef = useRef(null);
  const dragStartY = useRef(null);
  const dragStartScroll = useRef(0);

  useEffect(() => { setR(restaurant); setSheetMode("peek"); setShowShareMenu(false); setShowMapMenu(false); setPeekHeight(220); }, [restaurant]);

  // Dynamically calculate peek height based on actual height up to the "Add to My Places" block
  useEffect(() => {
    if (!mobile || !peekEndRef.current || !sheetRef.current) return;
    const measure = () => {
      const sheetTop = sheetRef.current.getBoundingClientRect().top;
      const endBottom = peekEndRef.current.getBoundingClientRect().bottom;
      const h = Math.ceil(endBottom - sheetTop) + 32; // bottom margin
      setPeekHeight(Math.max(h, 220));
    };
    // Measure after render (for async content like images)
    requestAnimationFrame(measure);
  });

  // Close dropdowns on outside click + ESC
  useEffect(() => {
    if (!showShareMenu && !showMapMenu && !showBestMenu) return;
    const handleClick = () => { setShowShareMenu(false); setShowMapMenu(false); setShowBestMenu(false); };
    const handleKey = (e) => { if (e.key === "Escape") handleClick(); };
    // Register on next tick to prevent current click event propagation
    const timer = setTimeout(() => {
      document.addEventListener("click", handleClick);
      document.addEventListener("keydown", handleKey);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [showShareMenu, showMapMenu, showBestMenu]);

  const isPersonalMine = r.isPersonal && (!r.user_id || (user && r.user_id === user.user_id));
  const isOthersPlace  = r.isPersonal && r.user_id && user && r.user_id !== user.user_id;

  useEffect(() => {
    if (!r.isPersonal || !r.id) return;
    axios.get(`${API_BASE}/places/${r.id}/neighbors`)
      .then((res) => setNeighbors(res.data)).catch(() => {});
  }, [r?.id, r?.isPersonal, user?.user_id]);

  useEffect(() => {
    if (!r.isPersonal || !r.id) return;
    axios.get(`${API_BASE}/places/${r.id}/comments`)
      .then((res) => setComments(res.data)).catch(() => {});
  }, [r?.id, r?.isPersonal]);

  useEffect(() => {
    if (!r) return;
    setLiked(false); setLikeCount(r.like_count || 0);
    setComments([]); setShowComments(true); setNeighbors([]);
    setGalleryIdx(0); setReplyTo(null);
  }, [r?.id]); // eslint-disable-line

  // Sync like count when restaurant prop changes (e.g. after onDataChange refresh)
  useEffect(() => { setLikeCount(restaurant.like_count || 0); }, [restaurant.like_count]);
  // Fetch liked status for current user (also re-fetch when like_count changes from parent)
  useEffect(() => {
    if (!user || !r.isPersonal || !r.id) return;
    axios.get(`${API_BASE}/places/${r.id}/like-status`)
      .then((res) => { setLiked(res.data.liked); setLikeCount(res.data.like_count); })
      .catch(() => {});
  }, [r?.id, restaurant.like_count, user?.user_id]); // eslint-disable-line

  // Detect portrait/landscape for current gallery image
  const currentImgUrl = (r.photo_urls?.length ? r.photo_urls : (r.photo_url ? [r.photo_url] : []))[galleryIdx];
  useEffect(() => {
    if (!currentImgUrl) { setImgPortrait(false); return; }
    const img = new Image();
    img.onload = () => setImgPortrait(img.naturalHeight > img.naturalWidth);
    img.src = currentImgUrl;
  }, [currentImgUrl]); // eslint-disable-line

  const handleCenterMap = () => {
    if (!mapInstance || !r.lat || !r.lng || !window.google?.maps) return;
    const coord = { lat: r.lat, lng: r.lng };

    // Set zoom first, then pan to the place
    mapInstance.setZoom(17);
    mapInstance.setCenter(coord);

    // After setZoom/setCenter, adjust for panel offset
    window.google.maps.event.addListenerOnce(mapInstance, "idle", () => {
      const div = mapInstance.getDiv();
      const mapW = div.offsetWidth; // eslint-disable-line no-unused-vars
      const mapH = div.offsetHeight; // eslint-disable-line no-unused-vars

      let offsetLat = 0, offsetLng = 0;
      if (mobile) {
        // Shift up to account for bottom sheet
        offsetLat = 0.0015;
      } else {
        // Shift right to account for side panel
        const panelW = 360;
        const shiftPx = panelW / 2;
        const scale = Math.pow(2, mapInstance.getZoom());
        offsetLng = -(shiftPx * 360) / (256 * scale);
      }

      mapInstance.panTo({ lat: r.lat + offsetLat, lng: r.lng + offsetLng });
    });
  };

  const [heartAnim, setHeartAnim] = useState(false);
  const handleLike = async () => {
    if (!user || !r.isPersonal || !r.id) return;
    try {
      const res = await axios.post(`${API_BASE}/places/${r.id}/like`);
      setLiked(res.data.liked);
      setLikeCount(res.data.like_count);
      if (res.data.liked) { setHeartAnim(true); setTimeout(() => setHeartAnim(false), 300); }
      if (onDataChange) onDataChange();
    } catch (e) {}
  };

  const handleComment = async () => {
    if (!commentInput.trim() || !user) return;
    setSubmittingComment(true);
    try {
      await axios.post(`${API_BASE}/places/${r.id}/comments`, {
        content: commentInput.trim(),
        parent_id: replyTo?.id || null,
      });
      // Re-fetch full threaded comments
      const updated = await axios.get(`${API_BASE}/places/${r.id}/comments`);
      setComments(updated.data);
      setCommentInput("");
      setReplyTo(null);
      if (onDataChange) onDataChange();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to post comment");
    } finally { setSubmittingComment(false); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API_BASE}/comments/${commentId}`);
      // Re-fetch threaded comments to get correct structure
      const updated = await axios.get(`${API_BASE}/places/${r.id}/comments`);
      setComments(updated.data);
      if (onDataChange) onDataChange();
    } catch (e) {}
  };

  const handleEditSave = async (updated) => {
    const res = await axios.patch(
      `${API_BASE}/personal-places/${r.id}`,
      { folder_id: updated.folder_id, status: updated.status, rating: updated.rating, memo: updated.memo, photo_url: updated.photo_url, photo_urls: updated.photo_urls, instagram_post_url: updated.instagram_post_url }
    );
    const updatedPlace = res.data;
    setR((prev) => ({ ...prev, ...updatedPlace }));
    if (onPlaceUpdated) onPlaceUpdated({ ...r, ...updatedPlace });
    if (onDataChange) onDataChange();
  };

  const handleShare = () => {
    const url = `${FRONTEND_URL}/?place=${r.id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const handleWebShare = async () => {
    const url = `${FRONTEND_URL}/?place=${r.id}`;
    const blob = await generateShareCard(r, user);
    const file = new File([blob], `${r.name}-ourcuration.png`, { type: "image/png" });

    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: r.name, url });
      } catch (e) {
        if (e.name !== "AbortError") handleShare();
      }
    } else {
      // Fallback: download image
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `${r.name}-ourcuration.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    }
  };

  // Check if registered in best picks
  const bestPickMatch = Object.values(myBestPicks).flat().find((pick) => {
    if (r.google_place_id && pick.google_place_id) return r.google_place_id === pick.google_place_id;
    return Math.abs(pick.lat - r.lat) < 0.001 && Math.abs(pick.lng - r.lng) < 0.001;
  });
  const isInBest = !!bestPickMatch;

  const handleBestCategorySelect = async (catKey) => {
    if (!onBestPickAdd) return;
    const res = await onBestPickAdd({
      category: catKey,
      name: r.name, address: r.address,
      lat: r.lat, lng: r.lng,
      google_place_id: r.google_place_id, google_maps_url: r.google_maps_url,
      personal_place_id: r.id,
    });
    setShowBestMenu(false);
  };

  const galleryUrls = r.photo_urls?.length ? r.photo_urls : (r.photo_url ? [r.photo_url] : []);
  const statusStyle = r.status ? STATUS_COLOR[r.status] : null;
  const googleMapsUrl = r.google_place_id
    ? `https://www.google.com/maps/place/?q=place_id:${r.google_place_id}`
    : (r.name ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.name)}` : null);
  const appleMapsUrl = r.lat && r.lng
    ? `https://maps.apple.com/?ll=${r.lat},${r.lng}&q=${encodeURIComponent(r.name || "")}`
    : (r.name ? `https://maps.apple.com/?q=${encodeURIComponent(r.name)}` : null);

  // -- Mobile drag handlers --
  const handleDragStart = (e) => {
    dragStartY.current = e.touches[0].clientY;
    dragStartScroll.current = sheetRef.current?.scrollTop || 0;
  };
  const handleDragEnd = (e) => {
    if (dragStartY.current === null) return;
    const diff = e.changedTouches[0].clientY - dragStartY.current;
    if (sheetMode === "peek" && diff < -40) {
      setSheetMode("full");
    } else if (sheetMode === "full" && diff > 60 && dragStartScroll.current <= 0) {
      setSheetMode("peek");
    } else if (sheetMode === "peek" && diff > 60) {
      onClose();
    }
    dragStartY.current = null;
  };

  // -- Shared content blocks --
  const HeaderBlock = (
    <div style={{ marginBottom: 10 }}>
      {r.category && (
        <p style={{
          margin: "0 0 3px", fontFamily: FL, fontSize: 14, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.12em", color: C.primary,
        }}>{r.category}</p>
      )}
      <h2 style={{
        margin: "0 0 4px", fontFamily: FH, fontSize: mobile ? 20 : 22,
        fontWeight: 700, color: C.onSurface, letterSpacing: "-0.01em",
        wordBreak: "break-word",
      }}>{r.name}</h2>
      {/* Address */}
      {r.address && (
        <p style={{
          margin: "0 0 6px", fontFamily: FL, fontSize: 15, color: C.outlineVariant,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{r.address}</p>
      )}
      {/* Map / Share buttons — left aligned */}
      <div style={{ display: "flex", gap: 4, justifyContent: "flex-start" }}>
        {(googleMapsUrl || appleMapsUrl) && (
          <div style={{ position: "relative" }}>
            <button onClick={() => { setShowMapMenu((v) => !v); setShowShareMenu(false); }} style={{
              display: "inline-flex", alignItems: "center", gap: 3, padding: "4px 8px",
              background: showMapMenu ? C.primaryContainer : C.surfaceLow,
              color: showMapMenu ? C.primary : C.onSurfaceVariant,
              border: "none", borderRadius: 5, fontFamily: FL, fontSize: 14, fontWeight: 600,
              cursor: "pointer", transition: "all 0.15s",
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>map</span>
              Map
            </button>
            {showMapMenu && (
              <div style={{
                position: "absolute", top: "100%", left: 0, marginTop: 4,
                display: "flex", gap: 4, zIndex: 10,
                background: C.surfaceLowest, borderRadius: 8, padding: 4,
                boxShadow: "0 4px 16px rgba(47,52,48,0.15)",
                animation: "panelFadeIn 0.12s ease-out",
              }}>
                {googleMapsUrl && (
                  <a href={googleMapsUrl} target="_blank" rel="noreferrer" onClick={() => setShowMapMenu(false)} style={{
                    display: "inline-flex", alignItems: "center", gap: 4, padding: "7px 12px",
                    background: "#4285F4", color: "white",
                    border: "none", borderRadius: 6, fontFamily: FL, fontSize: 14, fontWeight: 600,
                    textDecoration: "none", whiteSpace: "nowrap",
                  }}>
                    <span style={{ fontWeight: 800, fontSize: 14 }}>G</span>
                    Google Maps
                  </a>
                )}
                {appleMapsUrl && (
                  <a href={appleMapsUrl} target="_blank" rel="noreferrer" onClick={() => setShowMapMenu(false)} style={{
                    display: "inline-flex", alignItems: "center", gap: 4, padding: "7px 12px",
                    background: "#333333", color: "white",
                    border: "none", borderRadius: 6, fontFamily: FL, fontSize: 14, fontWeight: 600,
                    textDecoration: "none", whiteSpace: "nowrap",
                  }}>
                    <span style={{ fontWeight: 800, fontSize: 14 }}>&#63743;</span>
                    Apple Maps
                  </a>
                )}
              </div>
            )}
          </div>
        )}
        {r.isPersonal && r.id && (
          <div style={{ position: "relative" }}>
            <button onClick={() => { setShowShareMenu((v) => !v); setShowMapMenu(false); }} style={{
              display: "inline-flex", alignItems: "center", gap: 3, padding: "4px 8px",
              background: showShareMenu ? C.primaryContainer : C.surfaceLow,
              color: showShareMenu ? C.primary : C.onSurfaceVariant,
              border: "none", borderRadius: 5, fontFamily: FL, fontSize: 14, fontWeight: 600,
              cursor: "pointer", transition: "all 0.15s",
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>share</span>
              {t("place.share")}
            </button>
            {showShareMenu && (
              <div style={{
                position: "absolute", top: "100%", left: 0, marginTop: 4,
                display: "flex", flexWrap: "wrap", gap: 4, zIndex: 10,
                background: C.surfaceLowest, borderRadius: 8, padding: 4,
                boxShadow: "0 4px 16px rgba(47,52,48,0.15)",
                animation: "panelFadeIn 0.12s ease-out",
                maxWidth: 280,
              }}>
                <button onClick={() => { handleShare(); setShowShareMenu(false); }} style={{
                  display: "inline-flex", alignItems: "center", gap: 4, padding: "7px 12px",
                  background: copied ? C.primaryContainer : C.surfaceLow,
                  color: copied ? C.primary : C.onSurfaceVariant,
                  border: "none", borderRadius: 6, fontFamily: FL, fontSize: 14, fontWeight: 600,
                  cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s",
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>
                    {copied ? "check" : "link"}
                  </span>
                  {copied ? t("place.copied") : t("place.copy")}
                </button>
                <button onClick={async () => { setShowShareMenu(false); await handleWebShare(); }} style={{
                  display: "inline-flex", alignItems: "center", gap: 4, padding: "7px 12px",
                  background: "linear-gradient(135deg, #ede0d5, #faf9f6)",
                  color: C.primary, border: "none", borderRadius: 6,
                  fontFamily: FL, fontSize: 14, fontWeight: 600,
                  cursor: "pointer", whiteSpace: "nowrap",
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>ios_share</span>
                  {t("place.share")}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Add to best */}
        {isPersonalMine && user && (
          <div data-tutorial="add-to-best" style={{ position: "relative" }}>
            <button onClick={() => {
              if (isInBest) {
                if (window.confirm("Remove from best picks?") && onBestPickRemove && bestPickMatch) onBestPickRemove(bestPickMatch.id);
              } else {
                const hasPhoto = r.photo_urls?.length > 0 || !!r.photo_url;
                if (!hasPhoto) { alert(t("best.photo_required")); return; }
                setShowBestMenu((v) => !v); setShowShareMenu(false); setShowMapMenu(false);
              }
            }} style={{
              display: "inline-flex", alignItems: "center", gap: 3, padding: "4px 8px",
              background: isInBest ? "#FEF3CD" : (showBestMenu ? C.primaryContainer : C.surfaceLow),
              color: isInBest ? "#BA7517" : (showBestMenu ? C.primary : C.onSurfaceVariant),
              border: "none", borderRadius: 5, fontFamily: FL, fontSize: 14, fontWeight: 600,
              cursor: "pointer", transition: "all 0.15s",
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14, fontVariationSettings: isInBest ? "'FILL' 1" : "'FILL' 0" }}>star</span>
              {t("place.add_to_bests")}
            </button>
            {showBestMenu && (
              <div onClick={(e) => e.stopPropagation()} style={{
                position: "absolute", top: "100%", left: 0, marginTop: 4,
                display: "flex", flexDirection: "column", gap: 2, zIndex: 10,
                background: C.surfaceLowest, borderRadius: 8, padding: 6,
                boxShadow: "0 4px 16px rgba(47,52,48,0.15)",
                animation: "panelFadeIn 0.12s ease-out", minWidth: 120,
              }}>
                {BEST_CATEGORIES.map((cat) => (
                  <button key={cat.key} onClick={() => handleBestCategorySelect(cat.key)} style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "7px 10px",
                    background: "transparent", border: "none", borderRadius: 6,
                    fontFamily: FL, fontSize: 14, fontWeight: 600, color: C.onSurfaceVariant,
                    cursor: "pointer", whiteSpace: "nowrap", textAlign: "left",
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = C.surfaceLow}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >{cat.emoji} {cat.label}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const AddToMyPlaceBlock = isOthersPlace && user ? (
    <button onClick={() => setAddToMyPlaceOpen(true)} style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
      width: "100%", padding: "11px", marginBottom: 12,
      background: C.primary, color: "#fff6ef", border: "none", borderRadius: 8,
      fontFamily: FL, fontSize: 16, fontWeight: 700, cursor: "pointer",
      transition: "opacity 0.15s",
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add_location_alt</span>
      {t("search.add_to_my_places")}
    </button>
  ) : null;

  const StatusBlock = r.isPersonal && r.status ? (
    <div style={{
      background: C.surfaceLow, borderRadius: 10, padding: "12px 14px", marginBottom: 12,
    }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: r.memo ? 8 : 0 }}>
        <span style={{
          fontFamily: FL, fontSize: 14, fontWeight: 600, padding: "3px 9px", borderRadius: 5,
          background: statusStyle?.bg || C.container, color: statusStyle?.color || C.onSurfaceVariant,
        }}>{STATUS_LABEL[r.status] || r.status}</span>
        {r.rating > 0 && (
          <span style={{
            fontFamily: FL, fontSize: 14, padding: "3px 9px",
            background: C.primaryContainer, color: C.primary, borderRadius: 5, fontWeight: 600,
          }}>{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
        )}
        {r.owner_nickname && isOthersPlace && (
          <span style={{ fontFamily: FL, fontSize: 14, color: C.outlineVariant }}>by {r.owner_nickname}</span>
        )}
      </div>
      {r.memo && (
        <p style={{
          margin: 0, fontFamily: FH, fontStyle: "italic", fontSize: 16,
          color: C.onSurfaceVariant, lineHeight: 1.7,
        }}>"{r.memo}"</p>
      )}
    </div>
  ) : null;

  const GalleryBlock = galleryUrls.length > 0 ? (
    <div style={{ position: "relative", marginBottom: 12, borderRadius: 10, overflow: "hidden" }}>
      <div style={{ width: "100%", aspectRatio: imgPortrait ? "3/4" : "4/3", background: "#f0efec", transition: "aspect-ratio 0.2s" }}>
        <img src={galleryUrls[galleryIdx]} alt={r.name}
          onLoad={(e) => setImgPortrait(e.target.naturalHeight > e.target.naturalWidth)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      </div>
      {galleryUrls.length > 1 && (
        <>
          {galleryIdx > 0 && (
            <button onClick={() => setGalleryIdx((i) => i - 1)} style={{
              position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)",
              width: 28, height: 28, borderRadius: "50%",
              background: "rgba(0,0,0,0.35)", border: "none", color: "white",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_left</span>
            </button>
          )}
          {galleryIdx < galleryUrls.length - 1 && (
            <button onClick={() => setGalleryIdx((i) => i + 1)} style={{
              position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
              width: 28, height: 28, borderRadius: "50%",
              background: "rgba(0,0,0,0.35)", border: "none", color: "white",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_right</span>
            </button>
          )}
          <div style={{
            position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)",
            display: "flex", gap: 4,
          }}>
            {galleryUrls.map((_, i) => (
              <div key={i} style={{
                width: 5, height: 5, borderRadius: "50%",
                background: i === galleryIdx ? "white" : "rgba(255,255,255,0.45)",
              }} />
            ))}
          </div>
        </>
      )}
    </div>
  ) : null;

  const InstaBlock = r.instagram_post_url ? (
    <a href={r.instagram_post_url} target="_blank" rel="noreferrer" style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 12px",
      background: C.surfaceLow, color: C.primary, borderRadius: 7,
      fontFamily: FL, fontSize: 14, fontWeight: 600, textDecoration: "none", marginBottom: 12,
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>photo_camera</span>
      Instagram post
    </a>
  ) : null;

  const NeighborsBlock = neighbors.length > 0 ? (
    <div style={{ marginBottom: 12 }}>
      <p style={{
        fontFamily: FL, fontSize: 12, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.15em",
        color: C.outlineVariant, margin: "0 0 6px",
      }}>Also saved by ({neighbors.length})</p>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {neighbors.map((n) => (
          <div key={n.user_id} style={{
            display: "flex", alignItems: "center", gap: 5,
            background: C.surfaceLow, borderRadius: 6, padding: "4px 8px",
          }}>
            <div style={{
              width: 18, height: 18, borderRadius: "50%",
              background: `linear-gradient(135deg, ${C.primaryDim}, ${C.primary})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, color: "white", fontWeight: 700, flexShrink: 0, fontFamily: FL,
            }}>{n.nickname?.[0]?.toUpperCase()}</div>
            <span style={{ fontFamily: FL, fontSize: 14, color: C.onSurfaceVariant }}>{n.nickname}</span>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  const pickers = r.pickers || [];
  const PickersBlock = pickers.length > 0 ? (
    <div style={{ marginBottom: 12 }}>
      <p style={{
        fontFamily: FL, fontSize: 12, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.15em",
        color: C.outlineVariant, margin: "0 0 6px",
      }}>Saved by ({pickers.length})</p>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {pickers.map((p) => (
          <button
            key={p.user_id}
            onClick={() => onViewUserProfile && onViewUserProfile(p.nickname)}
            style={{
              display: "flex", alignItems: "center", gap: 5,
              background: C.surfaceLow, borderRadius: 6, padding: "4px 8px",
              border: "none", cursor: "pointer", transition: "background 0.12s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = C.container}
            onMouseLeave={(e) => e.currentTarget.style.background = C.surfaceLow}
          >
            <div style={{
              width: 18, height: 18, borderRadius: "50%",
              background: p.profile_photo_url
                ? `url(${p.profile_photo_url}) center/cover`
                : `linear-gradient(135deg, ${C.primaryDim}, ${C.primary})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, color: "white", fontWeight: 700, flexShrink: 0, fontFamily: FL,
            }}>{!p.profile_photo_url && p.nickname?.[0]?.toUpperCase()}</div>
            <span style={{ fontFamily: FL, fontSize: 14, color: C.onSurfaceVariant }}>{p.nickname}</span>
          </button>
        ))}
      </div>
    </div>
  ) : null;

  const SocialBlock = r.isPersonal && r.id ? (
    <div style={{ marginTop: 4 }}>
      {/* Action bar — feed style */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <button onClick={handleLike} style={{
          display: "flex", alignItems: "center", gap: 4,
          background: "none", border: "none", cursor: "pointer", padding: 0,
          color: liked ? "#D4537E" : C.onSurfaceVariant, transition: "color 0.15s",
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill={liked ? "#D4537E" : "none"} stroke={liked ? "#D4537E" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ transition: "transform 0.3s cubic-bezier(0.17,0.89,0.32,1.49)", transform: heartAnim ? "scale(1.3)" : "scale(1)" }}>
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </button>
        <button onClick={() => commentInputRef.current?.focus()} style={{
          display: "flex", alignItems: "center", gap: 4,
          background: "none", border: "none", cursor: "pointer", padding: 0,
          color: C.onSurfaceVariant,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 22 }}>chat_bubble_outline</span>
        </button>
        <button onClick={handleCenterMap} style={{
          display: "flex", alignItems: "center", gap: 4,
          background: "none", border: "none", cursor: "pointer", padding: 0,
          color: C.onSurfaceVariant,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 22 }}>location_on</span>
        </button>
      </div>

      {/* Like count */}
      {likeCount > 0 && (
        <p style={{ margin: "0 0 4px", fontFamily: FL, fontSize: 14, fontWeight: 700, color: C.onSurface }}>
          {likeCount} {likeCount === 1 ? "like" : "likes"}
        </p>
      )}

      {/* Comment count */}
      {comments.length > 0 && (
        <p style={{ margin: "0 0 4px", fontFamily: FL, fontSize: 14, color: C.outlineVariant }}>
          {comments.length} {comments.length === 1 ? "comment" : "comments"}
        </p>
      )}

      {/* Comments expanded */}
      {showComments && (
        <div style={{ marginTop: 6 }}>
          {comments.length === 0 && (
            <p style={{ margin: "0 0 8px", fontFamily: FL, fontSize: 14, color: C.outlineVariant, fontStyle: "italic" }}>
              Be the first to comment
            </p>
          )}
          {comments.map((c) => (
            <PanelCommentThread key={c.id} comment={c} depth={0} user={user}
              onReply={(c) => { setReplyTo(c); setTimeout(() => commentInputRef.current?.focus(), 0); }}
              onDelete={handleDeleteComment}
            />
          ))}
          {/* Reply indicator */}
          {replyTo && (
            <div style={{
              display: "flex", alignItems: "center", gap: 6, padding: "4px 0",
              fontFamily: FL, fontSize: 14, color: C.outlineVariant,
            }}>
              <span>Replying to {replyTo.author_nickname}</span>
              <button onClick={() => setReplyTo(null)} style={{
                background: "none", border: "none", cursor: "pointer",
                fontFamily: FL, fontSize: 14, color: C.outlineVariant, padding: 0,
              }}>✕</button>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: replyTo ? 0 : 8 }}>
            <input
              className="comment-input"
              ref={commentInputRef}
              value={commentInput}
              onChange={(e) => setCommentInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleComment()}
              placeholder={replyTo ? `Reply to ${replyTo.author_nickname}...` : "Add a comment..."}
              style={{
                flex: 1, padding: "6px 0",
                background: "none", border: "none", borderBottom: `1px solid ${C.container}`,
                fontFamily: FL, fontSize: 14, color: C.onSurface, outline: "none",
              }}
            />
            {commentInput.trim() && (
              <button onClick={handleComment} disabled={submittingComment} style={{
                background: "none", border: "none", cursor: "pointer",
                fontFamily: FL, fontSize: 14, fontWeight: 700, color: C.primary,
                padding: "6px 0", opacity: submittingComment ? 0.5 : 1,
              }}>Post</button>
            )}
          </div>
        </div>
      )}
    </div>
  ) : null;

  // ===============================================================
  // Mobile layout: bottom sheet (peek <-> full)
  // ===============================================================
  if (mobile) {
    return (
      <>
        <div
          ref={sheetRef}
          onTouchStart={handleDragStart}
          onTouchEnd={handleDragEnd}
          style={{
            position: "fixed",
            bottom: 64,
            left: 0, right: 0,
            height: sheetMode === "full" ? "80vh" : peekHeight,
            background: C.bg,
            borderRadius: "18px 18px 0 0",
            boxShadow: "0 -8px 40px rgba(47,52,48,0.12)",
            zIndex: 30,
            overflowX: "hidden",
            overflowY: sheetMode === "full" ? "auto" : "hidden",
            WebkitOverflowScrolling: "touch",
            transition: "height 0.3s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          {/* Handle */}
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px", cursor: "grab" }}>
            <div style={{ width: 32, height: 3, background: C.container, borderRadius: 2 }} />
          </div>

          <div style={{ padding: "6px 20px 24px" }}>
            {/* Header + Close/Edit */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>{HeaderBlock}</div>
              <div style={{ display: "flex", gap: 4, flexShrink: 0, marginTop: 2 }}>
                {isPersonalMine && (
                  <IconBtn icon="edit" onClick={() => setEditModalOpen(true)} title={t("place.edit")} size={32} />
                )}
                {canGoBack && onGoBack && (
                  <IconBtn icon="arrow_back" onClick={onGoBack} title="Back" size={32} />
                )}
                <IconBtn icon="close" onClick={onClose} title={t("place.close")} size={32} />
              </div>
            </div>

            {/* Status / Rating */}
            {StatusBlock}

            {/* Add to my places */}
            {AddToMyPlaceBlock}
            <div ref={peekEndRef} />

            {/* Photo gallery (below status) */}
            {GalleryBlock}

            {/* Instagram */}
            {InstaBlock}

            {/* Likes / Comments */}
            {SocialBlock}

            {/* Also saved by / Pickers */}
            {NeighborsBlock}
            {PickersBlock}
          </div>

          {/* Scroll hint arrow in peek state */}
          {sheetMode === "peek" && (
            <div
              onClick={() => setSheetMode("full")}
              style={{
                position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)",
                display: "flex", flexDirection: "column", alignItems: "center",
                cursor: "pointer", opacity: 0.4,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: C.outlineVariant }}>
                expand_less
              </span>
            </div>
          )}
        </div>

        {editModalOpen && (
          <SavePlaceModal
            place={r} editMode
            onSave={(updated) => { handleEditSave(updated); setEditModalOpen(false); }}
            onClose={() => setEditModalOpen(false)}
            onDelete={() => { onHide(r.id, r.isPersonal); setEditModalOpen(false); }}
          />
        )}
        {addToMyPlaceOpen && (
          <SavePlaceModal
            place={{ name: r.name, address: r.address, lat: r.lat, lng: r.lng, google_place_id: r.google_place_id, google_maps_url: r.google_maps_url, category: r.category }}
            onSave={() => { setAddToMyPlaceOpen(false); if (onDataChange) onDataChange(); }}
            onClose={() => setAddToMyPlaceOpen(false)}
          />
        )}
      </>
    );
  }

  // ===============================================================
  // Desktop layout: vertical panel to the right of the sidebar
  // ===============================================================
  return (
    <>
      <div style={{
        position: "fixed",
        top: 0,
        left: sidebarWidth,
        width: 325,
        height: "100vh",
        background: C.bg,
        boxShadow: "4px 0 24px rgba(47,52,48,0.08)",
        zIndex: 35,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        animation: "slideInDetail 0.22s cubic-bezier(0.16,1,0.3,1)",
      }}>
        {/* Top bar */}
        <div style={{
          position: "sticky", top: 0, zIndex: 5,
          background: "rgba(250,249,246,0.92)",
          backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
          padding: "20px 22px 14px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: `1px solid ${C.container}`,
        }}>
          <h1 style={{ margin: 0, fontFamily: FH, fontStyle: "italic", fontSize: 18, color: C.primary, letterSpacing: "-0.02em" }}>
            Place details
          </h1>
          <div style={{ display: "flex", gap: 4 }}>
            {isPersonalMine && (
              <IconBtn icon="edit" onClick={() => setEditModalOpen(true)} title={t("place.edit")} size={28} />
            )}
            {canGoBack && onGoBack && (
              <IconBtn icon="arrow_back" onClick={onGoBack} title="Back" size={28} />
            )}
            <IconBtn icon="close" onClick={onClose} title={t("place.close")} size={28} />
          </div>
        </div>

        <div style={{ padding: "16px 20px 24px" }}>
          {/* Header */}
          {HeaderBlock}

          {/* Status / Rating */}
          {StatusBlock}

          {/* Add to my places */}
          {AddToMyPlaceBlock}

          {/* Photo gallery */}
          {GalleryBlock}

          {/* Instagram */}
          {InstaBlock}

          {/* Likes / Comments */}
          {SocialBlock}

          {/* Also saved by */}
          {NeighborsBlock}
          {PickersBlock}
        </div>
      </div>

      {editModalOpen && (
        <SavePlaceModal
          place={r} editMode
          onSave={(updated) => { handleEditSave(updated); setEditModalOpen(false); }}
          onClose={() => setEditModalOpen(false)}
          onDelete={() => { onHide(r.id, r.isPersonal); setEditModalOpen(false); }}
        />
      )}
      {addToMyPlaceOpen && (
        <SavePlaceModal
          place={{ name: r.name, address: r.address, lat: r.lat, lng: r.lng, google_place_id: r.google_place_id, google_maps_url: r.google_maps_url, category: r.category }}
          onSave={() => { setAddToMyPlaceOpen(false); if (onDataChange) onDataChange(); }}
          onClose={() => setAddToMyPlaceOpen(false)}
        />
      )}

      <style>{`
        @keyframes slideInDetail {
          from { opacity: 0; transform: translateX(-16px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes panelFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .comment-input { font-size: 14px !important; }
      `}</style>
    </>
  );
}

function PanelCommentThread({ comment, depth, user, onReply, onDelete }) {
  const { t } = useTranslation();
  return (
    <>
      <div style={{ marginBottom: 4, paddingLeft: depth * 20, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontFamily: FL, fontSize: 14, color: "#2f3430", lineHeight: 1.5 }}>
            <span style={{ fontWeight: 700 }}>{comment.author_nickname}</span>{" "}
            <span style={{ color: "#5c605c" }}>{comment.content}</span>
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 1 }}>
            {user && (
              <button onClick={() => onReply({ id: comment.id, author_nickname: comment.author_nickname })} style={{
                background: "none", border: "none", cursor: "pointer", padding: 0,
                fontFamily: FL, fontSize: 12, color: "#8a8e8a",
              }}>Reply</button>
            )}
            {comment.user_id === user?.user_id && (
              <button onClick={() => onDelete(comment.id)} style={{
                background: "none", border: "none", cursor: "pointer", padding: 0,
                fontFamily: FL, fontSize: 12, color: "#8a8e8a",
              }}>{t("place.delete")}</button>
            )}
          </div>
        </div>
      </div>
      {comment.replies?.map((r) => (
        <PanelCommentThread key={r.id} comment={r} depth={depth + 1} user={user} onReply={onReply} onDelete={onDelete} />
      ))}
    </>
  );
}

function IconBtn({ icon, onClick, title, danger = false, size = 32 }) {
  return (
    <button onClick={onClick} title={title} style={{
      width: size, height: size,
      background: danger ? "rgba(158,66,44,0.07)" : C.surfaceLow,
      border: "none", borderRadius: "50%",
      display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer", flexShrink: 0,
      color: danger ? C.error : C.onSurfaceVariant,
      transition: "background 0.15s",
    }}
      onMouseEnter={(e) => e.currentTarget.style.background = danger ? "rgba(158,66,44,0.12)" : C.container}
      onMouseLeave={(e) => e.currentTarget.style.background = danger ? "rgba(158,66,44,0.07)" : C.surfaceLow}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{icon}</span>
    </button>
  );
}
